import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { Link } from "react-router-dom";

export default function ServiceAgreementPage() {
  return (
    <LegalLayout title="Service Agreement" updated="June 1, 2026" current="/service-agreement">
      <p>
        This Service Agreement (the "Agreement") is entered into between NextGen Resolution Experts ("Provider,"
        "we," "us") and the subscribing business entity ("Client," "you"). It governs the provision of our
        AI-powered IT helpdesk software and hosting services (the "Service"). By paying the setup fee or using the
        Service, the Client agrees to this Agreement.
      </p>

      <LegalSection heading="1. The Service (The Deal)">
        <p>
          Provider will supply, host, and maintain the NextGen Resolution Experts helpdesk platform, including the
          AI support assistant, ticketing system, knowledge base, and employee login portal. Provider is responsible
          for platform hosting, uptime maintenance, and software updates. The Service is provided as a hosted
          subscription — no software is transferred or installed on Client systems.
        </p>
      </LegalSection>

      <LegalSection heading="2. Fees & Payment">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Setup fee:</strong> a one-time onboarding and implementation fee of $499, due upfront before service activation.</li>
          <li><strong>Subscription fee:</strong> $499 per 30-day billing period, billed in advance, beginning on the activation date.</li>
          <li>Payments are due on the first day of each billing period. Accounts more than 7 days past due may be suspended until payment is received.</li>
          <li>All fees are exclusive of applicable taxes, which are the Client's responsibility.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. Term & Cancellation">
        <p>
          This Agreement continues on rolling 30-day periods until cancelled. <strong>Either party may cancel by
          providing at least thirty (30) days' written notice</strong> to the other party (email to
          support@nextgenresolutionexperts.com is sufficient). The Client remains responsible for all fees accruing
          through the end of the notice period. Fees already paid are handled per the{" "}
          <Link to="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link>.
        </p>
      </LegalSection>

      <LegalSection heading="4. Ownership">
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Provider owns the platform.</strong> All software, source code, AI configurations, designs, and documentation comprising the Service are and remain the exclusive property of NextGen Resolution Experts. No ownership rights are transferred under this Agreement.</li>
          <li><strong>Client owns its data.</strong> All customer records, tickets, and content submitted by the Client or its end users remain the property of the Client. Upon termination, the Client may request a data export within 30 days.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. AI Assistant — Limitation of Liability">
        <p>
          The Service includes automated AI responses. The Client acknowledges that AI output may occasionally be
          inaccurate. <strong>Provider shall not be liable for any error, statement, commitment, or approval made by
          the AI assistant — including, without limitation, any refund, discount, credit, or policy exception the AI
          incorrectly approves or implies.</strong> The Client is solely responsible for reviewing and confirming
          any AI-suggested action before honoring it with its own customers. Provider's total aggregate liability
          under this Agreement shall not exceed the fees paid by the Client in the three (3) months preceding the
          claim.
        </p>
      </LegalSection>

      <LegalSection heading="6. Client Responsibilities">
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide accurate business and billing information;</li>
          <li>Ensure its employees and end users comply with our <Link to="/terms" className="text-blue-600 hover:underline">Terms of Service</Link>;</li>
          <li>Maintain its own customer-facing policies (including any refund policy it offers its own customers);</li>
          <li>Keep account credentials secure.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="7. Service Levels & Support">
        <p>
          Provider targets 99.5% monthly uptime, excluding scheduled maintenance. Support requests may be submitted
          via the in-app helpdesk or by email and are handled during standard business hours, with priority response
          for service-down incidents.
        </p>
      </LegalSection>

      <LegalSection heading="8. Confidentiality">
        <p>
          Each party agrees to keep the other party's non-public business information confidential and to use it
          only as necessary to perform under this Agreement.
        </p>
      </LegalSection>

      <LegalSection heading="9. Termination for Cause">
        <p>
          Provider may terminate immediately if the Client materially breaches this Agreement (including non-payment
          or violation of the Acceptable Use rules) and fails to cure within 10 days of written notice.
        </p>
      </LegalSection>

      <LegalSection heading="10. General">
        <p>
          This Agreement is the entire agreement between the parties regarding the Service and supersedes prior
          discussions. It may only be modified in writing. If any provision is found unenforceable, the remainder
          stays in effect. Questions:{" "}
          <a href="mailto:support@nextgenresolutionexperts.com" className="text-blue-600 hover:underline">
            support@nextgenresolutionexperts.com
          </a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
